/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/service.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

/* eslint no-continue: 0 */

import i18n from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import AuditItemTypes from './definitions/item-types';
import { AuditSearchField, AuditSearchOperator, AuditServiceType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { panelsAndTablesHistory } from '@queries/generated/audit.generated';

export interface AuditRecord {
    id: string;
    action: string;
    created: string;
    updated: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    reason: null | string;
    area: string;
    description: string;
    studyId: string;
    objectType: string;
    data: string;
    before: string;
    itemId: string;
    parentId: string;
    itemRecordId: number;
    system: boolean;
    recordId: number;
    after: string;
    userId: string;
    userIpAddress: string;
    originService: AuditServiceType;
    auditId?: string;
    serviceItemId?: number;
    __typename: string;
}

export type ArrayOfObjectsV2 = Record<
    string,
    {
        valueResolver: (recordData: unknown, index: number) => string | null;
        compareField?: string;
        valueComparer?: (oldRecordData: unknown, newRecordData: unknown) => boolean;
    }
>;

const getSearchConditions = (search: any) => {
    if (!search) return [];

    const conditions = [];

    if (search.studies?.length) {
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_STUDY_ID,
            values: search.studies.map((study: any) => study.id),
        });
    }
    if (search.type)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_DATA_TYPE,
            value: search.type,
        });

    // Add conditions for Between and User
    if (search.startDate || search.endDate)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_CREATED,
            value: search.startDate ? moment(search.startDate).format('DD-MMM-YYYY') : null,
            value2: search.endDate ? moment(search.endDate).format('DD-MMM-YYYY') : null,
        });

    if (search.byFirst)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_USER_FIRSTNAME,
            value: search.byFirst,
        });

    if (search.byLast)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_USER_LAST_NAME,
            value: search.byLast,
        });

    if (search.byEmail)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_USER_EMAIL,
            value: search.byEmail,
        });

    if (search.action)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_ACTION,
            value: search.action,
        });

    if (search.reason)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_REASON,
            value: search.reason,
        });

    if (search.name)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_DESCRIPTION,
            value: search.name,
        });

    if (search.recordId)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_RECORD_ID,
            value: search.recordId,
        });

    if (search.userId)
        conditions.push({
            operator: AuditSearchOperator.AND,
            field: AuditSearchField.AUDIT_USER_ID,
            value: search.userId,
        });

    return conditions;
};

const stringify = (o: any, showDateWithTime = false) => {
    if (o === null || o === undefined) return '';

    // This means there are no audit changes to the properties of the object
    if (
        typeof o === 'object' &&
        ((Object.keys(o).length === 1 && o.T) ||
            Object.keys(o).length === 0 ||
            Object.values(o).every((value: any) => value === null || value === undefined))
    )
        return '';

    if (o.$oid === Constants.ObjectId.Empty) return '';

    if (o.$date) {
        const format = showDateWithTime ? 'DD-MMM-YYYY HH:mm:ss UTC' : 'DD-MMM-YYYY';
        // This was seen with Study audit log records, where they have no before date, but the value "0001-01-01T00:00:00.00+00:00"
        // is in mongo. When this is serialized, it results in an object with a $numberLong property as opposed to an ISO date
        // When moment parses the object, it results in today's date, which we don't want. Need specific handling for $numberLong property
        if (Object.hasOwn(o.$date, '$numberLong')) {
            const number = Number.parseFloat(o.$date.$numberLong);
            if (isNaN(number)) return null;
            const date = moment(number);
            return date.year() > 1 ? date.utc().format(format) : null;
        }
        return moment(o.$date).year() > 1 ? moment(o.$date).utc().format(format) : null;
    }

    if (o === false) return 'No';

    if (o === true) return 'Yes';

    return JSON.stringify(o);
};

const translationOrValue = (value: string) => {
    if (!value) return value;

    return i18n.t(`auditLog:${value}`, { defaultValue: value });
};

const getFieldChangeItemsAuditRecord = (
    oldRecord: Record<string, any> = {},
    newRecord: Record<string, any> = {},
    nestedFields: string[] | null = null,
    customRenderer: any = null,
) => {
    const ignoreKeys = (key: string) =>
        [
            'RecordId',
            'CurrentUserId',
            'CreatedByUserId',
            'CreatedByName',
            'CreatedDate',
            'UpdatedByUserId',
            'UpdatedByName',
            'UpdatedDate',
            '_id',
            'StudyId',
        ].indexOf(key) < 0;
    return getFieldChangeItemsWork(oldRecord, newRecord, nestedFields, customRenderer, ignoreKeys, []);
};

const getFieldChangeItems = (
    oldRecord: Record<string, any> = {},
    newRecord: Record<string, any> = {},
    nestedFields: string[] | null = null,
    customRenderer: any = null,
    dateTimeFieldKeys?: string[],
) => {
    const ignoreKeys = (key: string) => ['T', 'A', 'D', 'H'].indexOf(key) < 0;
    return getFieldChangeItemsWork(oldRecord, newRecord, nestedFields, customRenderer, ignoreKeys, dateTimeFieldKeys);
};

const getFieldChangeItemsWork = (
    oldRecord: Record<string, any> = {},
    newRecord: Record<string, any> = {},
    nestedFields: string[] | null = null,
    customRenderer: any = null,
    ignoreKeys?: (key: string) => boolean,
    dateTimeFieldKeys?: string[],
) => {
    const defaultRow = (oldRec: any, newRec: any, key: string, ignoreNested: boolean = false) => {
        if (!ignoreNested && nestedFields && nestedFields.indexOf(key) >= 0) {
            const subrender = (k: string) =>
                customRenderer
                    ? (customRenderer(`${key}.${k}`) ?? defaultRow(oldRec[key], newRec[key], k, true))
                    : defaultRow(oldRec[key], newRec[key], k, true);

            // Render all of the sub data
            const oldSubkeys = oldRec ? Object.keys(oldRec[key])?.filter(ignoreKeys) : [];
            const newSubkeys = newRec ? Object.keys(newRec[key])?.filter(ignoreKeys) : [];

            // First find all changes and removals by getting the old keys
            const subchanges: any = _.flatten(oldSubkeys.map((k: string) => subrender(k)));

            // Then additions
            newSubkeys.forEach((k: string) => {
                if (oldSubkeys.indexOf(k) < 0) subchanges.push(...subrender(k));
            });

            return subchanges;
        }

        let oldValue = null as any;
        let newValue = null as any;

        if (oldRec)
            oldValue =
                typeof oldRec[key] === 'object' || typeof oldRec[key] === 'boolean'
                    ? stringify(oldRec[key], dateTimeFieldKeys?.includes(key))
                    : oldRec[key];

        if (newRec)
            newValue =
                typeof newRec[key] === 'object' || typeof newRec[key] === 'boolean'
                    ? stringify(newRec[key], dateTimeFieldKeys?.includes(key))
                    : newRec[key];

        if (
            oldValue === newValue ||
            ((typeof oldValue === 'undefined' || oldValue === null || oldValue === '') &&
                (typeof newValue === 'undefined' || newValue === null || newValue === ''))
        )
            return [];

        return [
            {
                field: i18n.t(`auditLog:field-${key}`) ?? key,
                oldValue: translationOrValue(oldValue),
                newValue: translationOrValue(newValue),
            },
        ];
    };

    const render = (key: string) =>
        customRenderer
            ? (customRenderer(key) ?? defaultRow(oldRecord, newRecord, key))
            : defaultRow(oldRecord, newRecord, key);

    const oldKeys = oldRecord ? Object.keys(oldRecord).filter(ignoreKeys) : [];
    const newKeys = newRecord ? Object.keys(newRecord).filter(ignoreKeys) : [];

    // First find all changes and removals by getting the old keys
    const changes = _.flatten(oldKeys.map((key: string) => render(key)));

    // Then additions
    newKeys.forEach((key: string) => {
        if (oldKeys.indexOf(key) < 0) changes.push(...render(key));
    });

    if (!changes.length)
        return [
            {
                field: 'Item',
                oldValue: oldRecord ? JSON.stringify(oldRecord, null, 4) : '',
                newValue: newRecord ? JSON.stringify(newRecord, null, 4) : '',
            },
        ];

    return changes.filter((item: any) => item);
};

const getEnumChangeItem = (oldRecord: any, newRecord: any, field: string, values: string[], enumMarker?: string) => {
    if ((oldRecord && _.get(oldRecord, field) !== undefined) || (newRecord && _.get(newRecord, field) !== undefined))
        return {
            field: i18n.t(`auditLog:field-${field}`),
            oldValue: !oldRecord
                ? ''
                : _.get(oldRecord, field) !== undefined
                  ? i18n.t(`auditLog:${values[_.get(oldRecord, field)]}${enumMarker ?? ''}`)
                  : '',
            newValue: !newRecord
                ? ''
                : _.get(newRecord, field) !== undefined
                  ? i18n.t(`auditLog:${values[_.get(newRecord, field)]}${enumMarker ?? ''}`)
                  : '',
        };
};

const getLookupChangeItem = (
    oldRecord: any,
    newRecord: any,
    oldItem: any,
    newItem: any,
    field: string,
    valueResolver: any,
) => {
    if ((oldRecord && oldRecord[field]) || newRecord[field]) {
        const oldValue =
            oldItem || oldRecord
                ? valueResolver(oldItem, oldRecord)
                    ? valueResolver(oldItem, oldRecord)
                    : typeof oldRecord[field] === 'object' || typeof oldRecord[field] === 'boolean'
                      ? stringify(oldRecord[field])
                      : oldRecord[field]
                : '';
        const newValue =
            newItem || newRecord
                ? valueResolver(newItem, newRecord)
                    ? valueResolver(newItem, newRecord)
                    : typeof newRecord[field] === 'object' || typeof newRecord[field] === 'boolean'
                      ? stringify(newRecord[field])
                      : newRecord[field]
                : '';
        if ((oldValue && oldValue.toString().trim().length) || (newValue && newValue.toString().trim().length))
            return {
                field: i18n.t(`auditLog:field-${field}`),
                oldValue,
                newValue,
            };
    }
};

const getListOfObjects = (
    oldRecord: any,
    newRecord: any,
    oldItem: any,
    newItem: any,
    field: string,
    listOfObjects: any[],
) => {
    let oldValue = '';
    let newValue = '';
    const rows: {
        field: string;
        oldValue: string;
        newValue: string;
    }[] = [];
    listOfObjects.forEach((item: any) => {
        oldValue = '';
        newValue = '';
        if (oldRecord[field] || newRecord[field]) {
            if (
                (oldRecord[field] && Object.keys(oldRecord[field]).length) ||
                (newRecord[field] && Object.keys(newRecord[field]).length)
            ) {
                if (oldRecord[field] && Object.keys(oldRecord[field]).length && oldRecord[field][item] !== undefined)
                    oldValue = `${
                        typeof oldRecord[field][item] === 'boolean'
                            ? oldRecord[field][item] === true
                                ? 'Yes'
                                : 'No'
                            : (oldRecord[field][item] ?? ' ')
                    }`;
                if (newRecord[field][item] !== undefined)
                    newValue = `${
                        typeof newRecord[field][item] === 'boolean'
                            ? newRecord[field][item] === true
                                ? 'Yes'
                                : 'No'
                            : (newRecord[field][item] ?? ' ')
                    }`;
            } else {
                // object is empty
                oldValue = '';
                newValue = '';
            }
            if (oldValue.length || newValue.length)
                rows.push({
                    field: `${i18n.t(`auditLog:field-${field}`)} - ${i18n.t(`auditLog:field-${item}`)}`,
                    oldValue,
                    newValue,
                });
        } else {
            // case for ISMP
            if (Object.keys(oldRecord).length)
                oldValue = `${
                    typeof oldRecord[item] === 'boolean'
                        ? oldRecord[item] === true
                            ? 'Yes'
                            : 'No'
                        : (oldRecord[item] ?? '')
                }`;
            newValue = `${
                typeof newRecord[item] === 'boolean'
                    ? newRecord[item] === true
                        ? 'Yes'
                        : 'No'
                    : (newRecord[item] ?? '')
            }`;
            if (oldValue.length || newValue.length)
                rows.push({
                    field: i18n.t(`auditLog:field-${item}`),
                    oldValue,
                    newValue,
                });
        }
    });

    if (rows.length) return rows;
    return null;
};

const getObjectsArrayChangesRows = (
    field: string,
    oldRecord: Record<string, object[]>,
    newRecord: Record<string, object[]>,
    resolveValue: (val: object) => string,
) => {
    const oldObjectsArray = oldRecord[field]?.filter(Boolean) || [];
    const newObjectsArray = newRecord[field]?.filter(Boolean) || [];
    const oldResolvedValues = [...new Set(oldObjectsArray.map((obj) => resolveValue(obj)))];
    const newResolvedValues = [...new Set(newObjectsArray.map((obj) => resolveValue(obj)))];
    const oldDifferences = oldResolvedValues.filter((x) => !newResolvedValues.includes(x));
    const newDifferences = newResolvedValues.filter((x) => !oldResolvedValues.includes(x));
    const changedObjectsCount = Math.max(oldDifferences.length, newDifferences.length);

    const rows: { field: string; oldValue: any; newValue: any }[] = [];

    for (let i = 0; i < changedObjectsCount; i++) {
        rows.push({
            field: i18n.t(`auditLog:field-${field}`),
            oldValue: oldDifferences[i],
            newValue: newDifferences[i],
        });
    }

    return rows;
};

const getArrayOfObjects = (
    oldRecord: any,
    newRecord: any,
    oldItem: any,
    newItem: any,
    field: string,
    objValue: any,
    compareArrayOfObjectsFields?: string[],
) => {
    if (compareArrayOfObjectsFields?.includes(field)) {
        return getObjectsArrayChangesRows(field, oldRecord, newRecord, objValue.valueResolver);
    }

    const rows: any[] = [];

    if (
        newRecord &&
        Object.keys(newRecord).length &&
        newRecord[field] &&
        newRecord[field].length &&
        Object.keys(newRecord).length >= Object.keys(oldRecord).length
    ) {
        newRecord[field].forEach((item: any, index: number) => {
            let oldValue = '';
            let newValue = '';
            const oldRec =
                oldRecord && oldRecord[field] && Object.keys(oldRecord[field]) ? oldRecord[field][index] : null;
            if (objValue.valueResolver) {
                if (oldRec)
                    oldValue = objValue.valueResolver(
                        oldRec,
                        oldItem ? (oldItem[field] ? oldItem[field][index] : null) : null,
                        index,
                    );
                newValue = !field.toLowerCase().match('remove')
                    ? objValue.valueResolver(
                          item,
                          newItem ? (newItem[field] ? newItem[field][index] : null) : null,
                          index,
                      )
                    : null;
            } else {
                for (const property in item) {
                    if (oldRec)
                        oldValue += `${property}: ${
                            typeof oldRec[property] === 'boolean'
                                ? oldRec[property] === true
                                    ? 'Yes'
                                    : 'No'
                                : (oldRec[property] ?? '')
                        }; `;
                    newValue += `${property}: ${
                        typeof item[property] === 'boolean'
                            ? item[property] === true
                                ? 'Yes'
                                : 'No'
                            : (item[property] ?? '')
                    }; `;
                }
            }
            if (oldValue || newValue)
                rows.push({
                    field: i18n.t(`auditLog:field-${field}`),
                    oldValue,
                    newValue,
                });
        });
    } else if (oldRecord && Object.keys(oldRecord).length && oldRecord[field]) {
        oldRecord[field].forEach((item: any, index: number) => {
            let oldValue = '';
            let newValue = '';
            const newRec =
                newRecord && newRecord[field] && Object.keys(newRecord[field]) ? newRecord[field][index] : null;
            if (objValue.valueResolver) {
                oldValue = objValue.valueResolver(
                    item,
                    oldItem ? (oldItem[field] ? oldItem[field][index] : null) : null,
                    index,
                );
                if (newRec)
                    newValue = !field.toLowerCase().match('remove')
                        ? objValue.valueResolver(
                              newRec,
                              newItem ? (newItem[field] ? newItem[field][index] : null) : null,
                              index,
                          )
                        : null;
            } else {
                for (const property in item) {
                    oldValue += `${property}: ${
                        typeof item[property] === 'boolean'
                            ? item[property] === true
                                ? 'Yes'
                                : 'No'
                            : (item[property] ?? '')
                    }; `;
                    if (newRec)
                        newValue += `${property}: ${
                            typeof newRec[property] === 'boolean'
                                ? newRec[property] === true
                                    ? 'Yes'
                                    : 'No'
                                : (newRec[property] ?? '')
                        }; `;
                }
            }
            if (compareArrayOfObjectsFields?.includes(field)) {
                appendArrayObjectsFieldToAuditRows(rows, field, oldRecord, newRecord, objValue.valueResolver);
                return;
            }
            rows.push({
                field: i18n.t(`auditLog:field-${field}`),
                oldValue,
                newValue,
            });
        });
    }
    if (compareArrayOfObjectsFields?.includes(field)) {
        appendArrayObjectsFieldToAuditRows(rows, field, oldRecord, newRecord, objValue.valueResolver);
    }

    return rows;
};

const getArrayOfObjectsV2 = (
    field: string,
    oldRecord?: Record<string, unknown>,
    newRecord?: Record<string, unknown>,
    valueResolver?: (recordData: unknown, index: number) => string | null,
    idField?: string,
    valueComparer?: (oldRecordData: unknown, newRecordData: unknown) => boolean,
) => {
    if (!oldRecord?.[field] && !newRecord?.[field]) return;

    const rows = [];

    // Find edits and deletes
    const oldValues = (oldRecord?.[field] ?? []) as unknown[];
    const newValues = (newRecord?.[field] ?? []) as unknown[];

    for (let i = 0; i < oldValues.length; i++) {
        const val = oldValues[i];
        if (val == null) continue;

        let editedValueIndex = -1;
        if (idField) {
            // @ts-ignore
            editedValueIndex = newValues.findIndex((v) => v?.[idField] === val?.[idField]);
        } else editedValueIndex = newValues.findIndex((v) => v === val);
        if (editedValueIndex === i) {
            // If there's a value comparer, and the results are not different, continue
            if (valueComparer) {
                if (!valueComparer(val, newValues[editedValueIndex])) continue;
            } else continue;
        }

        rows.push({
            field: i18n.t(`auditLog:field-${field}`),
            oldValue: valueResolver?.(val, i),
            newValue: editedValueIndex > -1 ? valueResolver?.(newValues[editedValueIndex], editedValueIndex) : null,
        });
    }

    // Find adds
    for (let i = 0; i < newValues.length; i++) {
        const val = newValues[i];
        if (val == null) continue;

        let oldValueIndex = -1;
        if (idField) {
            // @ts-ignore
            oldValueIndex = oldValues.findIndex((v) => v?.[idField] === val?.[idField]);
        } else oldValueIndex = oldValues.findIndex((v) => v === val);

        if (oldValueIndex < 0)
            rows.push({
                field: i18n.t(`auditLog:field-${field}`),
                oldValue: null,
                newValue: valueResolver?.(val, i),
            });
    }

    return rows;
};

const getAssociationChangeItems = (
    oldRecord: any,
    newRecord: any,
    oldItem: any,
    newItem: any,
    field: string,
    itemField: string,
    associationName: string,
    nameResolver: any,
    customMessage?: string,
) => {
    const oldIds = oldRecord
        ? oldRecord[field]
            ? oldRecord[field]
                  .filter((i: any) => i !== null)
                  .map((item: any) => item.ReportId?.$oid ?? item.$oid ?? item)
            : []
        : [];
    const newIds = newRecord
        ? newRecord[field]
            ? newRecord[field]
                  .filter((i: any) => i !== null)
                  .map((item: any) => item.ReportId?.$oid ?? item.$oid ?? item)
            : []
        : [];
    const rows = [] as any;

    // Find the added
    newIds.forEach((id: any) => {
        if (oldIds.indexOf(id) < 0) {
            let newValue = null as any;
            if (newItem) {
                const childItem = newItem[itemField]
                    ?.filter((i: any) => i !== null)
                    .find((item: any) => item.reportId === id || item.id === id || item === id);
                if (childItem) newValue = nameResolver(childItem);
            }
            rows.push({
                field: customMessage ?? `${associationName} Association Added`,
                oldValue: null,
                newValue,
            });
        }
    });

    // Find the removed
    oldIds.forEach((id: any) => {
        if (newIds.indexOf(id) < 0) {
            let oldValue = null as any;
            if (oldItem) {
                const childItem = oldItem[itemField]
                    ?.filter((i: any) => i !== null)
                    .find((item: any) => item.id === id || item === id);
                if (childItem) oldValue = nameResolver(childItem);
            }
            rows.push({
                field: customMessage ?? `${associationName} Association Removed`,
                oldValue,
                newValue: null,
            });
        }
    });

    // Return the list of changes
    return rows;
};

export type RiqAssociationStatus = 'Pending' | 'Accepted' | 'Rejected' | 'AcceptedAndPromoted';

export type RiqAssociationConfig = {
    // Audit record field (e.g. RiqPendingVariableAssociations) -> the RIQ status that list represents
    statusByField: Record<string, RiqAssociationStatus>;
    // Merged lookup field on the history item (e.g. associatedRiqVariables); empty without RIQ read permission
    lookupField: string;
    // 'Ctq' or 'Risk' - selects the translation keys used for labels and the fallback text
    target: 'Ctq' | 'Risk';
    nameResolver: (item: any) => string | null | undefined;
};

const getRecordIds = (record: any, field: string): string[] =>
    (record?.[field] ?? []).filter((i: any) => i != null).map((item: any) => item.$oid ?? item);

const findRiqStatus = (record: any, config: RiqAssociationConfig, id: string) =>
    Object.keys(config.statusByField).find((field) => getRecordIds(record, field).includes(id));

/**
 * Describes changes to one of the RIQ association lists. The status comes from which list changed;
 * the merged lookup field is only used to resolve the id to display text. An id that moves between
 * lists is reported once (by the destination list) as a status change rather than a remove + add.
 */
const getRiqAssociationChangeItems = (
    oldRecord: any,
    newRecord: any,
    oldItem: any,
    newItem: any,
    field: string,
    config: RiqAssociationConfig,
) => {
    const status = config.statusByField[field];
    const oldIds = getRecordIds(oldRecord, field);
    const newIds = getRecordIds(newRecord, field);

    const describe = (id: string) => {
        const match = [...(newItem?.[config.lookupField] ?? []), ...(oldItem?.[config.lookupField] ?? [])].find(
            (item: any) => item?.id === id,
        );
        const name = match ? config.nameResolver(match) : null;
        return name ?? i18n.t(`auditLog:riq${config.target}Fallback`, { id });
    };
    const statusLabel = (s: RiqAssociationStatus) => i18n.t(`auditLog:RiqAssociationStatus-${s}`);
    const withStatus = (id: string, s: RiqAssociationStatus) => `${describe(id)} (${statusLabel(s)})`;

    const rows = [] as any;

    // Added to this list, either new or moved from another RIQ list (both values then carry their status)
    newIds.forEach((id) => {
        if (oldIds.includes(id)) return;
        const previousField = findRiqStatus(oldRecord, config, id);
        const moved = previousField && previousField !== field;
        rows.push({
            field: i18n.t(`auditLog:field-Riq${config.target}Association`, { status: statusLabel(status) }),
            oldValue: moved ? withStatus(id, config.statusByField[previousField]) : null,
            newValue: moved ? withStatus(id, status) : describe(id),
        });
    });

    oldIds.forEach((id) => {
        if (newIds.includes(id)) return;
        // Moved to another RIQ list - reported as a status change by that list
        if (findRiqStatus(newRecord, config, id)) return;
        rows.push({
            field: i18n.t(`auditLog:field-Riq${config.target}AssociationRemoved`),
            oldValue: withStatus(id, status),
            newValue: null,
        });
    });

    return rows;
};

const getPermissionsData = (oldRecord: any, newRecord: any, field: string) => {
    const oldPermissions = oldRecord
        ? oldRecord[field]
            ? oldRecord[field].filter((i: any) => i !== null).map((item: any) => item)
            : []
        : [];
    const newPermissions = newRecord
        ? newRecord[field]
            ? newRecord[field].filter((i: any) => i !== null).map((item: any) => item)
            : []
        : [];
    const rows = [] as any;

    const valueConvertor = (permission: any) => {
        if (permission?.Permissions !== undefined) {
            if (
                permission.Element === 'data-ram' ||
                permission.Element === 'data-cm' ||
                permission.Element === 'data-cm2' ||
                permission.Element === 'data-sm'
            )
                return permission.Permissions ? 'Read Write' : 'Read Only';
            if (permission.Element === 'data-internal-risks' || permission.Element === 'data-ram-compliance-report')
                return permission.Permissions ? 'View' : 'Hide';

            return permission.Permissions ? 'Active' : 'Inactive';
        }
        return '';
    };

    newPermissions.forEach((newPermission: any) => {
        const oldValue = oldPermissions.find((oldPermission: any) => oldPermission.Element === newPermission.Element);
        if (oldValue?.Permissions !== newPermission?.Permissions)
            rows.push({
                field: i18n.t(`auditLog:field-${newPermission.Element}`),
                oldValue: valueConvertor(oldValue),
                newValue: valueConvertor(newPermission),
            });
    });

    return rows;
};

const removePropertiesFromObject = (object: any, properties: string[]) => {
    const newObj = { ...object };
    properties.forEach((key: string) => {
        newObj[key] = undefined;
    });

    return newObj;
};

const getProcessedFieldChangeItems = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    field,
    enums,
    lookups,
    associations,
    listOfObjects,
    arrayOfObjects,
    enumMarkerByField,
    compareArrayOfObjectsFields,
    arrayOfObjectsV2,
    riqAssociations,
}: {
    oldRecord: Record<string, any>;
    newRecord: Record<string, any>;
    oldItem?: Record<string, any>;
    newItem?: Record<string, any>;
    field: string;
    enums?: Record<string, any>;
    lookups?: Record<string, any>;
    associations?: Record<string, any>;
    listOfObjects?: Record<string, any>;
    arrayOfObjects?: Record<string, any>;
    enumMarkerByField?: Record<string, any>;
    compareArrayOfObjectsFields?: string[];
    arrayOfObjectsV2?: ArrayOfObjectsV2;
    riqAssociations?: RiqAssociationConfig;
}) => {
    if (enums && enums[field])
        return [
            getEnumChangeItem(
                oldRecord,
                newRecord,
                field,
                enums[field],
                enumMarkerByField ? enumMarkerByField[field] : undefined,
            ),
        ];

    if (lookups && lookups[field]) {
        const values = getLookupChangeItem(oldRecord, newRecord, oldItem, newItem, field, lookups[field]);
        if (values) return [values];
    }

    if (riqAssociations?.statusByField[field])
        return getRiqAssociationChangeItems(oldRecord, newRecord, oldItem, newItem, field, riqAssociations);

    if (associations && associations[field])
        return getAssociationChangeItems(
            oldRecord,
            newRecord,
            oldItem,
            newItem,
            field,
            associations[field].itemField,
            associations[field].name,
            associations[field].nameResolver,
            associations[field].customMessage,
        );
    if (listOfObjects && listOfObjects[field])
        return getListOfObjects(oldRecord, newRecord, oldItem, newItem, field, listOfObjects[field]);

    if (arrayOfObjects && arrayOfObjects[field]) {
        return getArrayOfObjects(
            oldRecord,
            newRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjects[field],
            compareArrayOfObjectsFields,
        );
    }

    if (arrayOfObjectsV2 && arrayOfObjectsV2[field]) {
        const v2 = getArrayOfObjectsV2(
            field,
            oldRecord,
            newRecord,
            arrayOfObjectsV2[field].valueResolver,
            arrayOfObjectsV2[field].compareField,
            arrayOfObjectsV2[field].valueComparer,
        );
        return v2;
    }
};

const getItemHistory = ({ client, itemId, type, parentId, orgId, studyId, originService, serviceItemId }: any) => {
    const item = (AuditItemTypes as any)[type];
    if (!item || !item.query) return;

    if (orgId && studyId)
        return client
            .query({
                query: item.query,
                fetchPolicy: 'no-cache',
                variables: { id: itemId, organisationId: orgId, studyId },
            })
            .then((reply: any) => (reply.data ? reply.data[item.response] : null));
    return client
        .query({
            query: item.query,
            fetchPolicy: 'no-cache',
            variables: item.child
                ? { parentId, childId: itemId, originService, serviceItemId }
                : { id: itemId, originService, serviceItemId },
        })
        .then((reply: any) => (reply.data ? reply.data[item.response] : null));
};

const getPanelsAndTables = ({ client, query, orgId, studyId, timestamp }: any): Promise<panelsAndTablesHistory> =>
    client
        .query({ query, fetchPolicy: 'no-cache', variables: { organisationId: orgId, studyId, timestamp } })
        .then((data: any) => data.data);

type Change = {
    field: string;
    oldValue: string | number | boolean | null | undefined;
    newValue: string | number | boolean | null | undefined;
};

// Helper to skip typename fields
const isTypenameField = (key: string) => key.endsWith('__typename');

/**
 * Deep diff that flattens every change down to primitive leaf fields.
 * Treats `undefined` and `null` as equivalent and skips `__typename` keys.
 */
function diffObjects(obj1: any, obj2: any, parentKey = ''): Change[] {
    const changes: Change[] = [];

    const isPrimitive = (val: any) => val === null || typeof val !== 'object';
    const makePath = (key: string, parent: string) => (parent ? `${parent}.${key}` : key);

    const areEqual = (a: any, b: any): boolean => {
        if ((a === null || a === undefined) && (b === null || b === undefined)) {
            return true;
        }
        return a === b;
    };

    // --- Case 1: both primitives
    if (isPrimitive(obj1) && isPrimitive(obj2)) {
        if (!areEqual(obj1, obj2) && !isTypenameField(parentKey.split('.').pop()!)) {
            changes.push({
                field: parentKey,
                oldValue: obj1,
                newValue: obj2,
            });
        }
        return changes;
    }

    // --- Case 2: one primitive, one object
    if (isPrimitive(obj1) && !isPrimitive(obj2)) {
        flattenAll(obj2, parentKey, (field, value) => {
            if (!isTypenameField(field.split('.').pop()!) && !areEqual(obj1, value)) {
                changes.push({ field, oldValue: obj1, newValue: value });
            }
        });
        return changes;
    }

    if (!isPrimitive(obj1) && isPrimitive(obj2)) {
        flattenAll(obj1, parentKey, (field, value) => {
            if (!isTypenameField(field.split('.').pop()!) && !areEqual(value, obj2)) {
                changes.push({ field, oldValue: value, newValue: obj2 });
            }
        });
        return changes;
    }

    // --- Case 3: both arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        const max = Math.max(obj1.length, obj2.length);
        for (let i = 0; i < max; i++) {
            const val1 = obj1[i];
            const val2 = obj2[i];
            const path = `${parentKey}[${i}]`;
            changes.push(...diffObjects(val1, val2, path));
        }
        return changes;
    }

    // --- Case 4: both objects
    const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);
    for (const key of keys) {
        if (isTypenameField(key)) continue; // skip typename
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];
        const fullKey = makePath(key, parentKey);
        changes.push(...diffObjects(val1, val2, fullKey));
    }

    return changes;
}

/**
 * Helper: recursively flattens all primitive values in an object or array.
 */
function flattenAll(value: any, parentKey: string, callback: (path: string, val: any) => void) {
    const isPrimitive = (v: any) => v === null || typeof v !== 'object';

    if (isPrimitive(value)) {
        callback(parentKey, value);
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((v, i) => {
            flattenAll(v, `${parentKey}[${i}]`, callback);
        });
        return;
    }

    for (const key of Object.keys(value)) {
        if (isTypenameField(key)) continue; // skip typename
        const path = parentKey ? `${parentKey}.${key}` : key;
        flattenAll(value[key], path, callback);
    }
}

export {
    getSearchConditions,
    getFieldChangeItemsAuditRecord,
    getFieldChangeItems,
    getItemHistory,
    getEnumChangeItem,
    getLookupChangeItem,
    getAssociationChangeItems,
    getProcessedFieldChangeItems,
    getListOfObjects,
    getPermissionsData,
    removePropertiesFromObject,
    getPanelsAndTables,
    diffObjects,
};
